import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';
import { icon } from '../shared/components/icons';
import { paginate, filterBar, pager } from '../shared/lib/tableControls';

interface GapSummary {
  total_gaps: number;
  total_missed_revenue: number;
  properties_affected: number;
  avg_gap_nights: number;
  blocked_nights_excluded?: number;
}

interface GapItem {
  listing_id: string | number;
  listing_name: string;
  gap_start: string;
  gap_end: string;
  gap_nights: number;
  prev_booking: { check_in: string; check_out: string; channel: string; total_price: number | null } | null;
  next_booking: { check_in: string; check_out: string; channel: string; total_price: number | null } | null;
  missed_revenue: number | null;
  adr: number | null;
  min_stay: number | null;
}

interface TimelineReservation {
  id: string | number;
  check_in: string;
  check_out: string;
  channel: string;
}

interface TimelineGap {
  gap_start: string;
  gap_end: string;
  gap_nights: number;
  missed_revenue: number | null;
}

interface TimelineRow {
  listing_id: string | number;
  name: string;
  reservations: TimelineReservation[];
  gaps: TimelineGap[];
}

interface GapNightsData {
  summary: GapSummary;
  gaps: GapItem[];
  timeline: TimelineRow[];
}

export class GapNightsPage {
  private container: HTMLElement;
  private data: GapNightsData | null = null;
  private currentPage = 1;
  private readonly itemsPerPage = 20;
  private tooltip: HTMLDivElement | null = null;

  // Filters
  private propLimit: '15' | '30' | 'all' = '15';   // Top N properties by missed revenue
  private horizonDays: 30 | 60 | 90 = 90;          // Look-ahead window (re-fetched from server)
  private search = '';                             // free-text filter over property name (client-side)

  // Filtered view (derived from `data` via applyFilters)
  private viewGaps: GapItem[] = [];
  private viewTimeline: TimelineRow[] = [];
  private viewSummary: GapSummary = { total_gaps: 0, total_missed_revenue: 0, properties_affected: 0, avg_gap_nights: 0 };

  // Per-gap handled notes, keyed by `${listing_id}|${gap_start}|${gap_end}`.
  private notes: Record<string, { note: string; updated_at?: string }> = {};
  private boundActionClick = false;

  private gapKey(g: { listing_id: string | number; gap_start: string; gap_end: string }): string {
    return `${g.listing_id}|${g.gap_start}|${g.gap_end}`;
  }

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">

        <div class="dash-head">
          <h1>Gap Nights</h1>
          <p>Open, bookable nights between reservations — chase the gaps with the highest missed revenue first.</p>
        </div>

        <style>
          .gnt-kpi-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .gnt-timeline { position: relative; }
          .gnt-row { display: flex; align-items: center; border-bottom: 1px solid #f1f5f9; min-height: 36px; }
          .gnt-label { width: 180px; flex-shrink: 0; font-size: 12px; font-weight: 500; color: #334155; padding-right: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .gnt-bar { flex: 1; position: relative; height: 24px; }
          .gnt-segment { position: absolute; top: 4px; height: 16px; border-radius: 4px; }
          .gnt-booking { background: rgba(37,99,235,0.75); border: 1px solid var(--accent); }
          .gnt-gap { background: rgba(245,158,11,0.55); border: 1px solid rgba(217,119,6,0.6); height: 20px !important; top: 2px !important; cursor: pointer; z-index: 2; }
          .gnt-gap:hover { background: rgba(245,158,11,0.85); }
          .gnt-gap-orphan { background: rgba(245,158,11,0.95); border-color: #d97706; }
          .gnt-gap-done { background: rgba(22,163,74,0.75) !important; border-color: #15803d !important; animation: none !important; }
          .gnt-gap-done:hover { background: rgba(22,163,74,0.95) !important; }
          .gnt-today { position: absolute; top: 0; bottom: 0; width: 2px; background: #ef4444; opacity: 0.7; z-index: 3; pointer-events: none; }
          .gnt-action-cell { cursor: pointer; }
          .gnt-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 20px; }
          .gnt-modal { background: #fff; border-radius: 14px; padding: 20px 22px; width: 440px; max-width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
          .gnt-modal textarea:focus { outline: none; border-color: var(--accent); box-shadow: var(--focus-ring); }
          .gnt-header { display: flex; border-bottom: 2px solid #e2e8f0; margin-bottom: 4px; }
          .gnt-header-label { width: 180px; flex-shrink: 0; }
          .gnt-header-dates { flex: 1; position: relative; height: 20px; font-size: 10px; color: #94a3b8; }
          .gnt-header-tick { position: absolute; transform: translateX(-50%); }
          @keyframes gnt-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.65; } }
          .gnt-gap-orphan { animation: gnt-pulse 2.2s ease-in-out infinite; }
          .gnt-gap-orphan:hover { animation: none; }
          .gnt-tooltip {
            position: fixed; z-index: 9999; pointer-events: none;
            background: rgba(15,23,42,0.94); color: #f1f5f9;
            padding: 8px 12px; border-radius: 8px; font-size: 12px; line-height: 1.6;
            box-shadow: 0 4px 16px rgba(0,0,0,0.25); white-space: nowrap;
            display: none;
          }
          .gnt-toolbar { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 18px; }
          .gnt-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
          .gnt-field label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
          .gnt-select {
            padding: 7px 12px; border: 1px solid var(--border); border-radius: 8px;
            font-size: 13px; font-weight: 500; background: #fff; color: var(--text);
            cursor: pointer; min-width: 150px; max-width: 100%; transition: border-color 0.12s, box-shadow 0.12s;
          }
          .gnt-select:focus { outline: none; border-color: var(--accent); box-shadow: var(--focus-ring); }
          @media (max-width: 640px) {
            .gnt-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
            .gnt-label, .gnt-header-label { width: 110px; }
            .gnt-toolbar { gap: 10px; }
            .gnt-field { flex: 1 1 140px; }
            .gnt-select { min-width: 0; width: 100%; }
            #gnt-refresh-btn { margin-left: 0 !important; }
          }
        </style>

        <!-- Filter toolbar -->
        <div class="gnt-toolbar">
          <div class="gnt-field">
            <label for="gnt-prop-filter">Properties</label>
            <select id="gnt-prop-filter" class="gnt-select">
              <option value="15">Top 15</option>
              <option value="30">Top 30</option>
              <option value="all">All properties</option>
            </select>
          </div>
          <div class="gnt-field">
            <label for="gnt-horizon-filter">Horizon</label>
            <select id="gnt-horizon-filter" class="gnt-select">
              <option value="30">Next 30 days</option>
              <option value="60">Next 60 days</option>
              <option value="90" selected>Next 90 days</option>
            </select>
          </div>
          <button class="btn btn-sm btn-secondary" id="gnt-refresh-btn" style="margin-left:auto;">↺ Refresh</button>
        </div>

        <!-- KPI strip -->
        <div class="kpi-section-label">Revenue at risk</div>
        <div id="gnt-kpis" class="kpi-grid" style="margin-bottom: 8px;">
          <div class="kpi-card">${LoadingSpinner.render()}</div>
        </div>
        <div id="gnt-accuracy-note" style="font-size: 11px; color: var(--text-muted); margin-bottom: 16px; min-height: 14px;"></div>

        <!-- Wins: actioned gaps that got booked/filled since being noted -->
        <div id="gnt-wins"></div>

        <!-- Gap list table (primary view) -->
        <div class="pf-section" style="margin-bottom: 16px;">
          <div style="padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--border); flex-wrap: wrap; gap: 8px;">
            <div>
              <div style="font-size: 13px; font-weight: 600; color: var(--text);">Gap Opportunities</div>
              <div id="gnt-table-sub" style="font-size: 11px; color: var(--text-muted); margin-top: 2px;">Sorted by estimated missed revenue</div>
            </div>
          </div>

          <div style="padding: 12px 16px 0;" id="gnt-filter-bar"></div>

          <div class="table-scroll">
            <table style="min-width: 860px; width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background: #f8fafc;">
                  <th style="padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Property</th>
                  <th style="padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Gap Dates</th>
                  <th style="padding: 10px 16px; text-align: center; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Nights ${infoIcon('gap_nights')}</th>
                  <th style="padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Prev Booking</th>
                  <th style="padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Next Booking</th>
                  <th style="padding: 10px 16px; text-align: right; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Missed Revenue ${infoIcon('missed_revenue')}</th>
                  <th style="padding: 10px 16px; text-align: center; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border);">Action</th>
                </tr>
              </thead>
              <tbody id="gnt-table-body">
                <tr><td colspan="7" style="padding: 32px;">${LoadingSpinner.render()}</td></tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination (rendered via shared pager helper) -->
          <div style="padding: 0 16px;" id="gnt-pager"></div>
        </div>

        <!-- Gantt timeline panel (de-emphasized, collapsed by default) -->
        <details class="pf-section" style="margin-bottom: 16px;">
          <summary style="padding: 14px 16px; cursor: pointer; font-size: 13px; font-weight: 600; color: var(--text); list-style: revert;">Show timeline</summary>
          <div style="padding: 0 20px 16px;">
            <div style="margin-bottom: 12px;">
              <div style="font-size: 13px; font-weight: 600; color: var(--text);">Booking Timeline — Next <span id="gnt-gantt-horizon">90</span> Days</div>
              <div style="font-size: 11px; color: var(--text-muted); margin-top: 2px;">
                <span id="gnt-gantt-scope">Top 15 properties</span> by missed revenue ·
                <span style="display:inline-flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:rgba(245,158,11,0.55);display:inline-block;"></span>open night</span>
                <span style="margin-left:10px;display:inline-flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:rgba(245,158,11,0.95);display:inline-block;"></span>short orphan gap (≤3 nts)</span>
                <span style="margin-left:10px;display:inline-flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:rgba(37,99,235,0.75);display:inline-block;"></span>booked</span>
              </div>
            </div>
            <div id="gnt-gantt">
              <div style="padding: 32px; text-align: center;">${LoadingSpinner.render()}</div>
            </div>
          </div>
        </details>

        ${PageFooter.render([
          { abbr: 'Gap Night',   full: 'Open Gap Night',          note: 'a bookable-but-empty night — unbooked AND not owner-blocked / unavailable in the PMS calendar' },
          { abbr: 'Orphan Gap',  full: 'Orphan Gap (≤3 nights)',  note: 'a short open window wedged between two bookings — hardest to fill, highlighted brighter' },
          { abbr: 'Min Stay',    full: 'Minimum Stay',            note: 'min nights required to book those dates; if the gap is shorter than this, it cannot be booked until min-stay is lowered' },
          { abbr: 'ADR',         full: 'Average Daily Rate',      note: 'gap-specific nightly price from the calendar where available, else PriceLabs next/past-30 ADR' },
          { abbr: 'Missed Rev',  full: 'Estimated Missed Revenue', note: 'ADR × bookable-but-empty nights — revenue potentially lost if the nights stay unbooked' },
        ])}

      </div>

      <div id="gnt-tooltip" class="gnt-tooltip"></div>
    `;

    await this.loadData();
    this.bindEvents();
  }

  // ── Data ───────────────────────────────────────────────────

  private async loadData(): Promise<void> {
    try {
      const [res, noteRes] = await Promise.all([
        fetch(`/api/opportunities/gap-nights?days=${this.horizonDays}`),
        fetch('/api/opportunities/gap-notes'),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.data = await res.json();
      this.notes = noteRes.ok ? ((await noteRes.json()).notes || {}) : {};
      this.applyFilters();
    } catch (err) {
      console.error('Gap nights load failed:', err);
      const tbody = document.getElementById('gnt-table-body');
      if (tbody) tbody.innerHTML = `<tr><td colspan="7" style="padding:0;">${emptyState(
        'Could not load gap nights',
        'Something went wrong fetching the data. Try the Refresh button, or reload the page.',
        '⚠️',
      )}</td></tr>`;
      const gantt = document.getElementById('gnt-gantt');
      if (gantt) gantt.innerHTML = '<div style="padding:32px; text-align:center; color:var(--text-muted);">Failed to load timeline.</div>';
      const kpis = document.getElementById('gnt-kpis');
      if (kpis) kpis.innerHTML = '<div class="kpi-card"><div class="kpi-card-top"><span class="kpi-card-label">Error</span></div><div class="kpi-card-value">—</div></div>';
    }
  }

  // ── Filtering ──────────────────────────────────────────────

  /**
   * Derive the visible slice from `this.data` using the current filters.
   * Horizon (30/60/90) is already applied server-side via the `days` param;
   * here we apply the "Top N properties by missed revenue" limit and
   * recompute the KPI summary so every panel stays consistent with the table.
   */
  private applyFilters(): void {
    if (!this.data) return;

    // Rank properties by total missed revenue (gaps with no ADR contribute 0).
    const revByProp = new Map<string, number>();
    for (const g of this.data.gaps) {
      const key = String(g.listing_id);
      revByProp.set(key, (revByProp.get(key) || 0) + (g.missed_revenue || 0));
    }
    const ranked = [...revByProp.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
    const limit = this.propLimit === 'all' ? ranked.length : parseInt(this.propLimit, 10);
    const topSet = new Set(ranked.slice(0, limit));

    this.viewGaps = this.data.gaps.filter(g => topSet.has(String(g.listing_id)));
    this.viewTimeline = (this.data.timeline || []).filter(t => topSet.has(String(t.listing_id)));

    const totalMissed = this.viewGaps.reduce((s, g) => s + (g.missed_revenue || 0), 0);
    this.viewSummary = {
      total_gaps: this.viewGaps.length,
      total_missed_revenue: totalMissed,
      properties_affected: new Set(this.viewGaps.map(g => String(g.listing_id))).size,
      avg_gap_nights: this.viewGaps.length
        ? Math.round((this.viewGaps.reduce((s, g) => s + g.gap_nights, 0) / this.viewGaps.length) * 10) / 10
        : 0,
    };

    this.currentPage = 1;
    this.updateFilterLabels();
    this.renderKPIs();
    this.renderWins();
    this.renderGantt();
    this.renderTable();
  }

  // Gaps visible after the client-side search filter (applied on top of the
  // Top-N property slice in `viewGaps`). Property name match, case-insensitive.
  private searchedGaps(): GapItem[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.viewGaps;
    return this.viewGaps.filter(g => (g.listing_name || '').toLowerCase().includes(q));
  }

  private updateFilterLabels(): void {
    const scopeText = this.propLimit === 'all'
      ? `All ${this.viewSummary.properties_affected} properties`
      : `Top ${this.propLimit} properties`;
    const ganttHorizon = document.getElementById('gnt-gantt-horizon');
    const ganttScope = document.getElementById('gnt-gantt-scope');
    const tableSub = document.getElementById('gnt-table-sub');
    if (ganttHorizon) ganttHorizon.textContent = String(this.horizonDays);
    if (ganttScope) ganttScope.textContent = scopeText;
    if (tableSub) tableSub.textContent = `${scopeText} · next ${this.horizonDays} days · sorted by missed revenue`;

    const note = document.getElementById('gnt-accuracy-note');
    if (note) {
      const blocked = this.data?.summary.blocked_nights_excluded ?? 0;
      note.innerHTML = blocked > 0
        ? `✓ Availability-aware: <strong>${blocked.toLocaleString()}</strong> owner-blocked / unavailable night${blocked === 1 ? '' : 's'} excluded · gaps count only bookable-but-empty nights · ADR is date-specific where calendar pricing exists.`
        : `✓ Availability-aware: gaps count only bookable-but-empty nights (owner-blocked / unavailable nights are excluded).`;
    }
  }

  // ── KPI Strip ──────────────────────────────────────────────

  private renderKPIs(): void {
    const el = document.getElementById('gnt-kpis');
    if (!el || !this.data) return;
    const summary = this.viewSummary;
    const fmtRev = (v: number) => Number.isFinite(v) ? (v >= 1000 ? '$' + (v / 1000).toFixed(1) + 'k' : '$' + v) : '—';
    const fmtInt = (v: number) => Number.isFinite(v) ? String(v) : '—';
    const fmtNum = (v: number) => Number.isFinite(v) ? String(v) : '—';

    el.innerHTML = `
      <div class="kpi-card tone-bad">
        <div class="kpi-card-top"><span class="kpi-card-label">Open Gaps Found ${infoIcon('gap_nights')}</span><span class="kpi-card-ic">${icon('search', 17)}</span></div>
        <div class="kpi-card-value">${fmtInt(summary.total_gaps)}</div>
        <div class="kpi-card-cap">unbooked windows in range</div>
      </div>
      <div class="kpi-card tone-bad">
        <div class="kpi-card-top"><span class="kpi-card-label">Est. Missed Revenue ${infoIcon('missed_revenue')}</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
        <div class="kpi-card-value">${summary.total_missed_revenue > 0 ? fmtRev(summary.total_missed_revenue) : '—'}</div>
        <div class="kpi-card-cap">based on ADR ${infoIcon('adr')} × open nights</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-card-top"><span class="kpi-card-label">Properties Affected</span><span class="kpi-card-ic">${icon('home', 17)}</span></div>
        <div class="kpi-card-value">${fmtInt(summary.properties_affected)}</div>
        <div class="kpi-card-cap">listings with open nights</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-card-top"><span class="kpi-card-label">Avg Gap Size</span><span class="kpi-card-ic">${icon('calendar', 17)}</span></div>
        <div class="kpi-card-value">${fmtNum(summary.avg_gap_nights)}<span style="font-size:16px; font-weight:400; color:var(--text-muted);"> nights</span></div>
        <div class="kpi-card-cap">average gap length</div>
      </div>
    `;
  }

  // ── Wins (actioned gaps that got filled) ───────────────────

  /**
   * A "win" is a gap we noted/handled that is NO LONGER in the freshly-loaded
   * gaps list — meaning it got booked/filled after we actioned it. We detect
   * this by diffing the keys in `this.notes` against the keys currently present
   * in `this.data.gaps`. Null-safe and purely client-side.
   */
  private renderWins(): void {
    const el = document.getElementById('gnt-wins');
    if (!el) return;

    const noteKeys = Object.keys(this.notes || {});
    if (!noteKeys.length) { el.innerHTML = ''; return; }

    const currentKeys = new Set((this.data?.gaps || []).map(g => this.gapKey(g)));
    const wins = noteKeys.filter(k => !currentKeys.has(k));
    if (!wins.length) { el.innerHTML = ''; return; }

    const fmt = (s: string) => {
      const d = new Date(s + 'T00:00:00');
      return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const items = wins.slice(0, 6).map(k => {
      // Key shape: `${listing_id}|${gap_start}|${gap_end}`.
      const [, start, end] = k.split('|');
      const range = start && end ? `${fmt(start)} – ${fmt(end)}` : '';
      const note = this.notes[k]?.note || '';
      return `<span style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:600;background:#dcfce7;color:#15803d;border:1px solid #86efac;" title="${Formatters.escapeHtml(note)}">
        <span>✓ Booked</span>
        ${range ? `<span style="font-weight:500;color:#166534;">${Formatters.escapeHtml(range)}</span>` : ''}
      </span>`;
    }).join('');

    const more = wins.length > 6 ? `<span style="font-size:12px;color:#15803d;font-weight:600;">+${wins.length - 6} more</span>` : '';

    el.innerHTML = `
      <div style="background:#f0fdf4; border:1px solid #bbf7d0; border-radius:12px; padding:12px 16px; margin-bottom:16px;">
        <div style="font-size:13px; font-weight:700; color:#15803d; margin-bottom:8px;">🎉 ${wins.length} actioned gap${wins.length === 1 ? '' : 's'} got booked — filled after action</div>
        <div style="display:flex; flex-wrap:wrap; gap:8px; align-items:center;">
          ${items}
          ${more}
        </div>
      </div>`;
  }

  // ── Gantt Timeline ─────────────────────────────────────────

  private renderGantt(): void {
    const el = document.getElementById('gnt-gantt');
    if (!el || !this.data) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const windowMs = this.horizonDays * 86400000;

    const dateToPercent = (dateStr: string): number => {
      const d = new Date(dateStr + 'T00:00:00');
      const ms = d.getTime() - today.getTime();
      return Math.max(0, Math.min(100, (ms / windowMs) * 100));
    };

    const fmtShort = (dateStr: string) => {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const fmtRev = (v: number | null) =>
      Number.isFinite(v as number) ? ('$' + (v as number).toLocaleString()) : 'No ADR data';

    // Date header ticks: 6 evenly-spaced marks across the chosen horizon.
    const tickOffsets = [0, 1, 2, 3, 4, 5, 6].map(i => Math.round((this.horizonDays * i) / 6));
    const tickHtml = tickOffsets.map(days => {
      const d = new Date(today.getTime() + days * 86400000);
      const label = days === 0 ? 'Today' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const pct = (days / this.horizonDays) * 100;
      return `<span class="gnt-header-tick" style="left:${pct}%;">${Formatters.escapeHtml(label)}</span>`;
    }).join('');

    const rows = this.viewTimeline.map(row => {
      const bookingSegs = row.reservations.map(r => {
        const left = dateToPercent(r.check_in);
        const right = dateToPercent(r.check_out);
        const width = right - left;
        if (width <= 0) return '';
        const ch = Formatters.escapeHtml(r.channel || 'Direct');
        return `<div class="gnt-segment gnt-booking" style="left:${left.toFixed(2)}%; width:${width.toFixed(2)}%;" title="${ch}: ${r.check_in} – ${r.check_out}"></div>`;
      }).join('');

      const gapSegs = row.gaps.map(g => {
        const left = dateToPercent(g.gap_start);
        const right = dateToPercent(g.gap_end);
        const width = right - left;
        if (width <= 0) return '';
        const revText = fmtRev(g.missed_revenue);
        const key = this.gapKey({ listing_id: row.listing_id, gap_start: g.gap_start, gap_end: g.gap_end });
        const note = this.notes[key]?.note || '';
        const handled = !!note;
        const cls = handled ? ' gnt-gap-done' : (g.gap_nights <= 3 ? ' gnt-gap-orphan' : '');
        return `<div
          class="gnt-segment gnt-gap${cls}"
          style="left:${left.toFixed(2)}%; width:${Math.max(width, 0.5).toFixed(2)}%;"
          data-key="${Formatters.escapeHtml(key)}"
          data-name="${Formatters.escapeHtml(row.name)}"
          data-start="${Formatters.escapeHtml(g.gap_start)}"
          data-end="${Formatters.escapeHtml(g.gap_end)}"
          data-nights="${g.gap_nights}"
          data-rev="${Formatters.escapeHtml(revText)}"
          data-note="${Formatters.escapeHtml(note)}"
        ></div>`;
      }).join('');

      return `
        <div class="gnt-row">
          <div class="gnt-label" title="${Formatters.escapeHtml(row.name)}">${Formatters.escapeHtml(row.name)}</div>
          <div class="gnt-bar">
            ${bookingSegs}
            ${gapSegs}
          </div>
        </div>`;
    }).join('');

    const noDataMsg = !this.viewTimeline.length
      ? `<div style="padding:24px; text-align:center; color:var(--text-muted);">No gap data found in the next ${this.horizonDays} days.</div>`
      : '';

    el.innerHTML = `
      <div class="gnt-timeline">
        <div class="gnt-header">
          <div class="gnt-header-label"></div>
          <div class="gnt-header-dates">${tickHtml}</div>
        </div>
        ${rows}
        ${noDataMsg}
      </div>
    `;

    // Inject today line into each bar (needs to be after DOM is set)
    el.querySelectorAll<HTMLElement>('.gnt-bar').forEach(bar => {
      const todayLine = document.createElement('div');
      todayLine.className = 'gnt-today';
      todayLine.style.left = '0%';
      bar.appendChild(todayLine);
    });

    this.bindGanttTooltips();
  }

  private bindGanttTooltips(): void {
    const tooltip = document.getElementById('gnt-tooltip') as HTMLDivElement | null;
    if (!tooltip) return;
    this.tooltip = tooltip;

    const gantt = document.getElementById('gnt-gantt');
    if (!gantt) return;

    gantt.addEventListener('mousemove', (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('.gnt-gap');
      if (!target) {
        tooltip.style.display = 'none';
        return;
      }

      const name = target.dataset.name || '';
      const start = target.dataset.start || '';
      const end = target.dataset.end || '';
      const nights = target.dataset.nights || '';
      const rev = target.dataset.rev || '';
      const note = target.dataset.note || '';

      const fmtD = (s: string) => {
        const d = new Date(s + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };

      tooltip.innerHTML = `
        <div style="font-weight:600; margin-bottom:4px;">${Formatters.escapeHtml(name)}</div>
        <div>${Formatters.escapeHtml(fmtD(start))} – ${Formatters.escapeHtml(fmtD(end))} (${nights} night${nights === '1' ? '' : 's'})</div>
        <div style="color:#fcd34d;">Est. missed: ${Formatters.escapeHtml(rev)}</div>
        ${note
          ? `<div style="color:#86efac; margin-top:4px; max-width:240px; white-space:normal;">✓ ${Formatters.escapeHtml(note)}</div>`
          : `<div style="color:#94a3b8; margin-top:4px; font-size:11px;">Click to add a note →</div>`}
      `;

      const padding = 12;
      let x = e.clientX + padding;
      let y = e.clientY - 70;
      const rect = tooltip.getBoundingClientRect();
      if (x + 220 > window.innerWidth) x = e.clientX - 220 - padding;
      if (y < 0) y = e.clientY + padding;

      tooltip.style.left = x + 'px';
      tooltip.style.top = y + 'px';
      tooltip.style.display = 'block';
    });

    gantt.addEventListener('mouseleave', () => {
      tooltip.style.display = 'none';
    });
  }

  // ── Table ──────────────────────────────────────────────────

  private renderTable(): void {
    const tbody = document.getElementById('gnt-table-body');
    if (!tbody || !this.data) return;

    // Draw the search bar (count reflects the post-search total).
    const allGaps = this.searchedGaps();
    const pg = paginate(allGaps, this.currentPage, this.itemsPerPage);
    this.currentPage = pg.page; // adopt the clamped page

    const bar = document.getElementById('gnt-filter-bar');
    if (bar) {
      bar.innerHTML = filterBar({
        searchId: 'gnt-search',
        searchValue: this.search,
        searchPlaceholder: 'Search property…',
        countLabel: `${pg.total} gap${pg.total === 1 ? '' : 's'}`,
      });
    }
    this.bindFilterBar();
    this.renderPager(pg.page, pg.pages, pg.total);

    if (!allGaps.length) {
      tbody.innerHTML = `<tr><td colspan="7" style="padding:0;">${emptyState(
        this.search.trim() ? 'No gaps match your search' : 'No open gap nights',
        this.search.trim()
          ? 'No properties match that search. Clear the search box to see all gaps in range.'
          : `Every bookable night in the next ${this.horizonDays} days is either booked or owner-blocked. Widen the horizon or property scope to look further out.`,
        this.search.trim() ? '🔍' : '✅',
      )}</td></tr>`;
      return;
    }

    const page = pg.items;

    const nightChip = (n: number) => {
      const colors: Record<number, [string, string]> = {
        1: ['#dc2626', '#fee2e2'],
        2: ['#f59e0b', '#fef3c7'],
        3: ['#16a34a', '#dcfce7'],
      };
      const [fg, bg] = colors[n] || ['var(--accent)', 'var(--accent-soft)'];
      return `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${bg};color:${fg};font-size:12px;font-weight:700;">${n}</span>`;
    };

    const channelColor = (ch: string): string => {
      const lower = (ch || '').toLowerCase();
      if (lower.includes('airbnb')) return '#ff5a5f';
      if (lower.includes('vrbo') || lower.includes('homeaway')) return '#1e5c9b';
      if (lower.includes('booking')) return '#003580';
      return 'var(--accent)';
    };

    const channelPip = (ch: string) => {
      const color = channelColor(ch);
      const label = Formatters.escapeHtml(ch || 'Direct');
      return `<span style="display:inline-flex;align-items:center;gap:5px;font-size:12px;">
        <span style="width:7px;height:7px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>${label}
      </span>`;
    };

    const fmtDateRange = (start: string, end: string) => {
      const fmt = (s: string) => {
        const d = new Date(s + 'T00:00:00');
        return isNaN(d.getTime()) ? (s || '—') : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };
      return `${fmt(start)} – ${fmt(end)}`;
    };

    const chip = (bg: string, fg: string, bd: string, text: string) =>
      `<span style="display:inline-block;padding:3px 9px;border-radius:12px;font-size:11px;font-weight:600;background:${bg};color:${fg};border:1px solid ${bd};">${text}</span>`;

    const actionChip = (g: GapItem) => {
      const n = g.gap_nights;
      // The gap literally can't be booked: min-stay is longer than the gap.
      if (g.min_stay && n < g.min_stay) return chip('#eff6ff', '#1d4ed8', '#bfdbfe', `Reduce Min Stay (min ${g.min_stay})`);
      if (g.min_stay == null && n <= 2) return chip('#eff6ff', '#1d4ed8', '#bfdbfe', 'Reduce Min Stay?');
      if (n <= 6) return chip('#fdf4ff', '#7e22ce', '#e9d5ff', 'Discount');           // short gap
      return chip('#fff7ed', '#c2410c', '#fed7aa', 'Promote / Fill');                 // long open stretch
    };

    // Prev/Next cell: channel pip when bordered by a booking, muted "Open" otherwise.
    const bookingCell = (b: { channel: string } | null, where: 'start' | 'end') =>
      b ? channelPip(b.channel)
        : `<span style="font-size:12px;color:var(--text-muted);font-style:italic;">${where === 'start' ? 'Open (no prior booking)' : 'Open (no next booking)'}</span>`;

    // Handled gaps show a green chip with the note as tooltip; otherwise the
    // suggested action. The whole cell is clickable to open the note editor.
    const handledChip = (note: string) =>
      `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:12px;font-size:11px;font-weight:600;background:#dcfce7;color:#15803d;border:1px solid #86efac;" title="${Formatters.escapeHtml(note)}">✓ Handled</span>`;

    tbody.innerHTML = page.map(g => {
      const key = this.gapKey(g);
      const note = this.notes[key]?.note || '';
      const handled = !!note;
      return `
      <tr style="border-bottom: 1px solid #f1f5f9; ${handled ? 'background:#f0fdf4;' : ''}">
        <td style="padding: 11px 16px; font-size: 13px; font-weight: 500; max-width: 220px;"><span class="u-truncate" style="display:block;" title="${Formatters.escapeHtml(g.listing_name)}">${Formatters.escapeHtml(g.listing_name)}</span></td>
        <td style="padding: 11px 16px; font-size: 13px; color: var(--text-muted);">${Formatters.escapeHtml(fmtDateRange(g.gap_start, g.gap_end))}</td>
        <td style="padding: 11px 16px; text-align: center;">${nightChip(g.gap_nights)}</td>
        <td style="padding: 11px 16px;">${bookingCell(g.prev_booking, 'start')}</td>
        <td style="padding: 11px 16px;">${bookingCell(g.next_booking, 'end')}</td>
        <td style="padding: 11px 16px; text-align: right; font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; ${Number.isFinite(g.missed_revenue as number) ? 'color: var(--danger);' : 'color: var(--text-muted);'}">
          ${Number.isFinite(g.missed_revenue as number) ? '$' + (g.missed_revenue as number).toLocaleString() : '—'}
        </td>
        <td class="gnt-action-cell" data-gap-action="${Formatters.escapeHtml(key)}" title="Click to add a note / mark handled" style="padding: 11px 16px; text-align: center;">
          ${handled ? handledChip(note) : actionChip(g)}
        </td>
      </tr>`;
    }).join('');
  }

  // ── Pagination ─────────────────────────────────────────────

  private renderPager(page: number, pages: number, total: number): void {
    const el = document.getElementById('gnt-pager');
    if (!el) return;
    el.innerHTML = pager({ idPrefix: 'gnt', page, pages, total, pageSize: this.itemsPerPage });
    el.style.borderTop = pages > 1 ? '1px solid var(--border)' : '';
    document.getElementById('gnt-prev')?.addEventListener('click', () => {
      this.currentPage--;
      this.renderTable();
    });
    document.getElementById('gnt-next')?.addEventListener('click', () => {
      this.currentPage++;
      this.renderTable();
    });
  }

  // (Re)bind the search input and refocus it after a re-render, mirroring the
  // EventPricingPage pattern so typing in the search box never loses focus.
  private bindFilterBar(): void {
    const search = document.getElementById('gnt-search') as HTMLInputElement | null;
    if (!search) return;
    search.addEventListener('input', () => {
      this.search = search.value;
      this.currentPage = 1;
      this.renderTable();
      const next = document.getElementById('gnt-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });
  }

  // ── Events ─────────────────────────────────────────────────

  private bindEvents(): void {
    document.getElementById('gnt-refresh-btn')?.addEventListener('click', () => this.loadData());

    // Click an Action cell → open the note editor for that gap (delegated, since
    // the table body is re-rendered on every filter/page change). Bound once so
    // re-navigation doesn't stack duplicate handlers.
    if (!this.boundActionClick) {
      this.boundActionClick = true;
      this.container.addEventListener('click', (e) => {
        const el = e.target as HTMLElement;
        // Either the table's Action cell or an orange/green block on the timeline.
        const hit = el.closest<HTMLElement>('[data-gap-action]') || el.closest<HTMLElement>('.gnt-gap[data-key]');
        if (!hit) return;
        const key = hit.getAttribute('data-gap-action') || hit.getAttribute('data-key');
        const g = this.data?.gaps.find(x => this.gapKey(x) === key);
        if (g) this.openNoteEditor(g);
      });
    }

    // Properties filter (Top 15 / 30 / All) — purely client-side re-slice, no re-fetch.
    document.getElementById('gnt-prop-filter')?.addEventListener('change', (e) => {
      this.propLimit = (e.target as HTMLSelectElement).value as '15' | '30' | 'all';
      this.applyFilters();
    });

    // Horizon filter (30 / 60 / 90 days) — changes the server query, so re-fetch.
    document.getElementById('gnt-horizon-filter')?.addEventListener('change', (e) => {
      this.horizonDays = parseInt((e.target as HTMLSelectElement).value, 10) as 30 | 60 | 90;
      this.loadData();
    });
    // Prev/Next + search are (re)bound by renderPager / bindFilterBar on each
    // renderTable, since those controls are re-rendered from the shared helpers.
  }

  // ── Note editor ────────────────────────────────────────────

  private openNoteEditor(g: GapItem): void {
    const key = this.gapKey(g);
    const existing = this.notes[key]?.note || '';
    const fmt = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const meta = `${fmt(g.gap_start)} – ${fmt(g.gap_end)} · ${g.gap_nights} night${g.gap_nights === 1 ? '' : 's'}`
      + (Number.isFinite(g.missed_revenue as number) ? ` · $${(g.missed_revenue as number).toLocaleString()} at risk` : '');

    const overlay = document.createElement('div');
    overlay.className = 'gnt-modal-overlay';
    overlay.innerHTML = `
      <div class="gnt-modal">
        <div style="font-size:15px; font-weight:700; color:var(--text);">${Formatters.escapeHtml(g.listing_name)}</div>
        <div style="font-size:12px; color:var(--text-muted); margin:2px 0 14px;">${Formatters.escapeHtml(meta)}</div>
        <label for="gnt-note-input" style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Action note</label>
        <textarea id="gnt-note-input" rows="4" placeholder="e.g. Dropped min-stay to 1 night and applied a 10% orphan discount" style="width:100%; margin-top:6px; padding:10px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; resize:vertical; box-sizing:border-box;">${Formatters.escapeHtml(existing)}</textarea>
        <div style="font-size:11px; color:var(--text-muted); margin-top:6px;">Saving a note marks this gap <strong style="color:#16a34a;">handled</strong> (turns green). Clearing it reverts to open.</div>
        <div style="display:flex; gap:8px; align-items:center; margin-top:16px;">
          ${existing ? '<button class="btn btn-sm btn-ghost" id="gnt-note-clear" style="color:var(--danger); margin-right:auto;">Clear note</button>' : '<span style="margin-right:auto;"></span>'}
          <button class="btn btn-sm btn-secondary" id="gnt-note-cancel">Cancel</button>
          <button class="btn btn-sm btn-success" id="gnt-note-save">Save &amp; mark handled</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const close = () => overlay.remove();
    const input = overlay.querySelector('#gnt-note-input') as HTMLTextAreaElement;
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);

    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('#gnt-note-cancel')?.addEventListener('click', close);
    overlay.querySelector('#gnt-note-save')?.addEventListener('click', async () => {
      await this.saveNote(key, input.value.trim());
      close();
    });
    overlay.querySelector('#gnt-note-clear')?.addEventListener('click', async () => {
      await this.saveNote(key, '');
      close();
    });
    // Cmd/Ctrl+Enter saves
    input?.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        (overlay.querySelector('#gnt-note-save') as HTMLElement)?.click();
      }
    });
  }

  private async saveNote(key: string, note: string): Promise<void> {
    try {
      const res = await fetch('/api/opportunities/gap-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, note }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (note) this.notes[key] = { note, updated_at: new Date().toISOString() };
      else delete this.notes[key];
      this.renderTable();
      this.renderWins();
      this.renderGantt();
    } catch (err) {
      console.error('Failed to save gap note:', err);
      alert('Could not save the note. Please try again.');
    }
  }
}
